import { type FC, useState } from 'react'
import { useRouter } from '@egw/router'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { format } from 'date-fns'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { PageHeader, ModalDrawer, Field, Button, ButtonVariant } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { authsvsRemovePasskey, useAuthsvsPasskeys } from '@egw/api'
import { Logger } from '@egw/logger'
import { sendEvent } from '@egw/events'
import { passkeyService } from '@egw/auth'
import { getAaguidData } from '../../utils/aaguid'

const log = new Logger('PasskeyDetailsPage')

export const PasskeyDetailsPage: FC = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const { t } = useTranslation('features-auth')
  const { searchParams, navigate } = useRouter()
  const alias = searchParams().get('alias')

  const { data, isLoading, isError, refetch } = useAuthsvsPasskeys()

  const passkey = data?.find((p) => p.id === alias)
  const aaguidData = getAaguidData(passkey?.aaguid)

  const handleDelete = async (): Promise<void> => {
    sendEvent('user_passkeyDelete_click', {
      eventSource: 'egWealth',
      category: 'passkey-delete',
      subCategory: 'intent',
    })
    if (alias && passkey?.credentialId) {
      const credentialId = passkey.credentialId
      try {
        await authsvsRemovePasskey({
          credentialId,
        })
        sendEvent('eg_passkeyDelete_success', {
          eventSource: 'egWealth',
          category: 'passkey-delete',
          subCategory: 'result',
        })
        // Signal unknown credential after successful deletion
        try {
          await passkeyService.signalUnknownCredential(credentialId)
          sendEvent('eg_passkeyRemove_signal', {
            eventSource: 'egWealth',
            category: 'passkey-delete',
            subCategory: 'signal',
          })
        } catch (signalError) {
          log.warn('Failed to signal unknown credential after deletion', signalError)
        }
        await refetch()
      } catch (error) {
        sendEvent('eg_passkeyDelete_fail', {
          eventSource: 'egWealth',
          category: 'passkey-delete',
          subCategory: 'result',
          failureReason: JSON.stringify(error),
        })
        log.error('Failed to delete passkey', error)
      } finally {
        setIsDeleteModalOpen(false)
        navigate(WEALTH_ROUTES.SETTINGS_PASSKEY_DETAILS.backPath ?? WEALTH_ROUTES.SETTINGS_PASSKEY.path)
      }
    }
  }

  return (
    <>
      {!isLoading && passkey ? (
        <PageHeader
          title={aaguidData?.name ?? passkey.nickName ?? t('passkey.title')}
          subtitle={t('passkey.title')}
          backLinkUrl={WEALTH_ROUTES.SETTINGS_PASSKEY_DETAILS.backPath}
        />
      ) : null}

      <div className="egw-container pt-0">
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={5} height="80px" />
          </SkeletonTheme>
        ) : null}

        {isError ? (
          <div className="egw-container">
            <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
            <p className="text-error text-2xl font-light mb-10">{t('passkey.error_title')}</p>
            <p className="text-body text-xl">{t('passkey.error_message')}</p>
          </div>
        ) : null}

        {passkey ? (
          <div className="flex flex-col gap-2xs">
            <Field
              testId="createdOnItem_primary_btn"
              id="createdOnItem_primary_btn"
              label={t('passkey.created_on')}
              value={format(new Date(passkey.createdAt), t('date.long'))}
            />
            <hr className="border-light" />
            <Field
              label={t('passkey.last_used')}
              testId="lastUsedItem_primary_btn"
              id="lastUsedItem_primary_btn"
              value={format(new Date(passkey.updatedAt), t('date.long'))}
            />
            {passkey.browserName ? (
              <>
                <hr className="border-light" />
                <Field
                  testId="displayNameItem_primary_btn"
                  id="displayNameItem_primary_btn"
                  label={t('passkey.browser_name')}
                  value={passkey.browserName}
                />
              </>
            ) : null}

            {passkey.deviceOs ? (
              <>
                <hr className="border-light" />
                <Field
                  testId="deviceOsItem_primary_btn"
                  id="deviceOsItem_primary_btn"
                  label={t('passkey.device_os')}
                  value={passkey.deviceOs}
                />
              </>
            ) : null}
          </div>
        ) : null}

        {data && data.length > 1 ? (
          <>
            {/* <hr className="mt-2xs border-light" /> */}
            <Button
              variant={ButtonVariant.Secondary}
              onClick={() => {
                setIsDeleteModalOpen(true)
              }}
              className="my-lg"
              testId="deletePasskey_secondary_btn"
              id="deletePasskey_secondary_btn"
            >
              {t('passkey.remove_passkey')}
            </Button>
          </>
        ) : null}
      </div>

      {passkey ? (
        <ModalDrawer
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false)
          }}
          title={t('passkey.remove_passkey')}
        >
          <p className="mb-2xs">
            {t('passkey.delete_message', {
              nickname:
                passkey.browserName && passkey.deviceOs
                  ? `${passkey.browserName} ${passkey.deviceOs}`
                  : t('passkey.unknown_device'),
            })}
          </p>
          <Button
            variant={ButtonVariant.Danger}
            onClick={() => void handleDelete()}
            testId="removePasskeyConfirm_destruction_btn"
          >
            {t('passkey.remove_passkey')}
          </Button>
        </ModalDrawer>
      ) : null}
    </>
  )
}
